import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { useBOAuth } from '../../context/BOAuthContext';
import { boAPI } from '../../lib/api';
import { toast } from 'sonner';
import { BOLayout } from './BOLayout';
import { ArrowLeft, Phone, Mail, MapPin, Calendar, Briefcase, CreditCard, Users, Heart, FileText } from 'lucide-react';

const formatNaira = (amount) => {
  if (!amount && amount !== 0) return '--';
  return new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN', minimumFractionDigits: 0 }).format(amount);
};
const paymentStatusColors = { paid_full: 'bg-green-100 text-green-800', on_schedule: 'bg-blue-100 text-blue-800', behind: 'bg-amber-100 text-amber-800', defaulted: 'bg-red-100 text-red-800' };
const paymentStatusLabels = { paid_full: 'Paid in Full', on_schedule: 'On Schedule', behind: 'Behind', defaulted: 'Defaulted' };
const planLabels = { full: 'Full Payment', '3_months': '3 Months', '6_months': '6 Months', '12_months': '12 Months', '24_months': '24 Months' };
const clientStatusColors = { active: 'bg-green-100 text-green-800', inactive: 'bg-neutral-100 text-neutral-500', completed: 'bg-blue-100 text-blue-800' };

const InfoRow = ({ icon: Icon, label, value }) => {
  if (!value) return null;
  return (
    <div className="flex items-start gap-3 py-2">
      <Icon className="h-4 w-4 text-neutral-400 mt-0.5 flex-shrink-0" />
      <div><p className="text-xs text-neutral-500">{label}</p><p className="font-medium text-neutral-800">{value}</p></div>
    </div>
  );
};

const BOClientDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await boAPI.getClient(id);
        setClient(res.data);
      } catch (e) { toast.error('Failed to load client'); navigate('/backoffice/clients'); }
      finally { setLoading(false); }
    };
    fetch();
  }, [id]);

  if (loading) {
    return <BOLayout><div className="flex items-center justify-center min-h-[400px]"><div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600" /></div></BOLayout>;
  }
  if (!client) return null;

  const balance = (client.total_price || 0) - (client.amount_paid || 0);
  const progressPercent = client.total_price ? Math.min(100, Math.round((client.amount_paid / client.total_price) * 100)) : 0;

  return (
    <BOLayout>
      <div className="space-y-6" data-testid="bo-client-detail">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/backoffice/clients')} className="p-2 rounded-lg hover:bg-neutral-100"><ArrowLeft className="h-5 w-5" /></button>
            <div>
              <h1 className="text-2xl sm:text-3xl font-serif font-bold text-neutral-900">{client.full_name}</h1>
              <div className="flex items-center gap-2 mt-1">
                <Badge className={clientStatusColors[client.client_status]}>{client.client_status}</Badge>
                <Badge className={paymentStatusColors[client.payment_status]}>{paymentStatusLabels[client.payment_status]}</Badge>
              </div>
            </div>
          </div>
          {client.assigned_plot_id && (
            <Button onClick={() => navigate(`/backoffice/allocation/${client.id}`)} variant="outline" className="rounded-xl" data-testid="generate-allocation-btn">
              <FileText className="h-4 w-4 mr-2" /> Allocation Letter
            </Button>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Personal Info */}
          <Card className="border-neutral-border shadow-card">
            <CardHeader><CardTitle className="text-lg">Personal Information</CardTitle></CardHeader>
            <CardContent className="space-y-1">
              <InfoRow icon={Phone} label="Phone" value={client.phone} />
              {client.alt_phone && <InfoRow icon={Phone} label="Alt Phone" value={client.alt_phone} />}
              <InfoRow icon={Mail} label="Email" value={client.email} />
              <InfoRow icon={MapPin} label="Address" value={client.address} />
              <InfoRow icon={Calendar} label="Date of Birth" value={client.date_of_birth} />
              <InfoRow icon={Briefcase} label="Occupation" value={client.occupation} />
              {(client.next_of_kin_name || client.next_of_kin_phone) && (
                <div className="border-t pt-3 mt-2">
                  <p className="text-xs font-semibold text-neutral-500 mb-2">NEXT OF KIN</p>
                  <InfoRow icon={Heart} label="Name" value={client.next_of_kin_name} />
                  {client.next_of_kin_relationship && <InfoRow icon={Users} label="Relationship" value={client.next_of_kin_relationship} />}
                  <InfoRow icon={Phone} label="Phone" value={client.next_of_kin_phone} />
                </div>
              )}
            </CardContent>
          </Card>

          {/* Payment Summary */}
          <Card className="border-neutral-border shadow-card">
            <CardHeader><CardTitle className="text-lg">Payment Summary</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><p className="text-xs text-neutral-500">Total Price</p><p className="text-xl font-bold">{formatNaira(client.total_price)}</p></div>
                <div><p className="text-xs text-neutral-500">Amount Paid</p><p className="text-xl font-bold text-green-700">{formatNaira(client.amount_paid)}</p></div>
                <div><p className="text-xs text-neutral-500">Balance</p><p className="text-xl font-bold text-red-600">{formatNaira(balance)}</p></div>
                <div><p className="text-xs text-neutral-500">No. of Plots</p><p className="text-lg font-semibold">{client.number_of_plots || 1}</p></div>
                <div><p className="text-xs text-neutral-500">Payment Plan</p><p className="text-lg font-semibold">{planLabels[client.payment_plan] || client.payment_plan}</p></div>
              </div>
              {client.total_price > 0 && (
                <div>
                  <div className="flex justify-between text-xs text-neutral-500 mb-1"><span>Progress</span><span>{progressPercent}%</span></div>
                  <div className="h-3 bg-neutral-100 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full transition-all ${progressPercent >= 100 ? 'bg-green-500' : progressPercent >= 50 ? 'bg-blue-500' : 'bg-amber-500'}`} style={{ width: `${progressPercent}%` }} />
                  </div>
                </div>
              )}
              {client.next_payment_due && (
                <div className="flex items-center gap-2 p-3 bg-amber-50 rounded-xl">
                  <CreditCard className="h-4 w-4 text-amber-600" />
                  <div><p className="text-xs text-amber-600">Next Payment Due</p><p className="font-semibold text-amber-800">{client.next_payment_due}</p></div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Plot Info */}
          <Card className="border-neutral-border shadow-card">
            <CardHeader><CardTitle className="text-lg">Plot Information</CardTitle></CardHeader>
            <CardContent>
              {client.plot_info ? (
                <div className="space-y-3">
                  <div><p className="text-xs text-neutral-500">Plot Number</p><p className="text-xl font-bold">{client.plot_info.plot_number}</p></div>
                  <div><p className="text-xs text-neutral-500">Estate</p><p className="font-semibold">{client.plot_info.estate_name}</p></div>
                  {client.plot_info.plot_size && <div><p className="text-xs text-neutral-500">Size</p><p>{client.plot_info.plot_size}</p></div>}
                  {client.purchase_date && <div><p className="text-xs text-neutral-500">Purchase Date</p><p>{client.purchase_date}</p></div>}
                </div>
              ) : (
                <p className="text-neutral-500 text-sm">No plot assigned yet</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Notes */}
        {client.notes && (
          <Card className="border-neutral-border shadow-card">
            <CardHeader><CardTitle className="text-lg">Notes</CardTitle></CardHeader>
            <CardContent><p className="text-neutral-700 whitespace-pre-wrap">{client.notes}</p></CardContent>
          </Card>
        )}
      </div>
    </BOLayout>
  );
};

export default BOClientDetail;
